import { EMOTE_MS, type Emote } from '../../shared/scenes.ts';
import { RUN_STEP_MS, WALK_STEP_MS, type CharacterAnimation, type CharacterOutfit } from '../../shared/characters.ts';
import type { EquipmentAbility } from '../../shared/items.ts';
import { PLAYER_GLOW, type LightSource } from '../../shared/lighting.ts';
import type { Direction, FollowerView, NpcView, PlayerView, Position } from '../../shared/protocol.ts';

const BUBBLE_MS = 4000;
/** Caresse du suiveur : durée du cœur qui s'élève au-dessus de lui. */
export const PET_MS = 1100;
/** Saut par-dessus une bordure : durée, et hauteur du saut (en cases). */
const JUMP_MS = 380;
const JUMP_HEIGHT = 0.5;
/** Petit saut sur place d'un acteur de scène qui prend la parole. */
const TALK_HOP_MS = 260;
const TALK_HOP_HEIGHT = 0.25;
/** Glissade : le personnage fait un quart de tour sur lui-même toutes les SPIN_MS millisecondes. */
const SPIN_MS = 90;
const SPIN: readonly Direction[] = ['down', 'left', 'up', 'right'];
/** Entre deux pas enchaînés, l'animation de marche continue au lieu de repasser par l'immobile. */
const MOTION_GRACE_MS = 80;

/** Position du monstre qui suit un joueur (case visée, départ du pas en cours, position rendue). */
export interface Trail {
  x: number;
  y: number;
  fromX: number;
  fromY: number;
  renderX: number;
  renderY: number;
  dir: Direction;
}

const directionOf = (dx: number, dy: number): Direction =>
  (Math.abs(dx) >= Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down'));

/** Joueur affiché : position logique (case) et position rendue interpolée pendant un pas. */
export class Entity {
  readonly id: number;
  readonly username: string;
  readonly color: string;
  dir: Direction;
  /** Apparence (null : personnage dessiné par défaut). */
  characterId: number | null;
  /** Tenue portée (apparence personnalisable ; null : celle par défaut de l'apparence). */
  outfit: CharacterOutfit | null;
  /** Le pas en cours est couru. */
  running: boolean;
  x: number;
  y: number;
  renderX: number;
  renderY: number;
  /** Capacités de ses équipements (des patins : il ne glisse pas sur la glace). */
  abilities: EquipmentAbility[];
  /** Lumière de son équipement (casque-lampe…), null : aucune. */
  light: LightSource | null;
  /** Lueur sans équipement (voir PLAYER_GLOW) ; null pour un PNJ. */
  glow: LightSource | null = PLAYER_GLOW;
  /** Glisse sur la glace : il tourne sur lui-même pendant ses pas, sans les animer (voir `facing`). */
  sliding = false;
  /** Hauteur du saut en cours, en cases (0 au sol) : le personnage est dessiné plus haut, sans changer de ligne. */
  hop = 0;
  /** Décalage horizontal en cases, pour le Vol en diagonale (la caméra ne le suit pas). */
  drift = 0;
  private jumping = false;
  private fromX: number;
  private fromY: number;
  private stepStartedAt = 0;
  private stepMs = WALK_STEP_MS;
  /** Début de la série de pas en cours, et fin du dernier pas : cadencent les animations. */
  private motionStartedAt = 0;
  private stoppedAt = 0;
  private bubbleText: string | null = null;
  private pettedAt = -Infinity;
  /** Émotion montrée dans une bulle (scène), depuis `at`. */
  private emoteShown: { emote: Emote; at: number } | null = null;
  /** Début du petit saut sur place en cours, null : aucun. */
  private hoppedAt: number | null = null;
  private bubbleUntil = 0;
  /** Monstre qui suit le joueur : il va sur la case que le joueur vient de quitter. */
  follower: FollowerView | null;
  /** {Monstre} sur lequel il surfe (dans l'eau) ; null : à pied. */
  surf: FollowerView | null;
  /** Acteur de scène qui est un monstre : dessiné comme un suiveur ; null : un personnage. */
  monster: NpcView['monster'] | null = null;
  readonly trail: Trail;

  constructor(player: PlayerView) {
    this.id = player.id;
    this.username = player.username;
    this.color = player.color;
    this.dir = player.dir;
    this.characterId = player.characterId;
    this.outfit = player.outfit;
    this.running = player.running;
    this.x = this.renderX = this.fromX = player.x;
    this.y = this.renderY = this.fromY = player.y;
    this.follower = player.follower;
    this.surf = player.surf;
    this.abilities = player.abilities;
    this.light = player.light;
    this.trail = { x: player.x, y: player.y, fromX: player.x, fromY: player.y, renderX: player.x, renderY: player.y, dir: player.dir };
  }

  /** Un PNJ, dessiné comme un dresseur (ou un monstre, acteur de scène) : sans suiveur, ni équipement, ni monture. */
  static fromNpc(npc: NpcView): Entity {
    const entity = new Entity({ ...npc, username: npc.name, follower: null, running: false, abilities: [], light: null, surf: null });
    entity.monster = npc.monster ?? null;
    entity.glow = null;
    return entity;
  }

  /** Reprend l'apparence et l'équipement envoyés par le serveur (ni la position, ni la direction). */
  applyView(player: PlayerView): void {
    Object.assign(this, { follower: player.follower, characterId: player.characterId, outfit: player.outfit, abilities: player.abilities, light: player.light, surf: player.surf });
  }

  /** Le joueur caresse son suiveur : un cœur s'élève au-dessus de lui. */
  pet(now: number): void {
    this.pettedAt = now;
  }

  /** Bulle d'émotion (❗ ❓…) au-dessus du personnage, pendant EMOTE_MS. */
  showEmote(emote: Emote, now: number): void {
    this.emoteShown = { emote, at: now };
  }

  /** Petit saut sur place (il prend la parole dans une scène), joué quand il ne marche pas. */
  hopInPlace(now: number): void {
    this.hoppedAt = now;
  }

  /** Émotion en cours et son avancement (0 → 1), null s'il n'y en a pas. */
  emoting(now: number): { emote: Emote; progress: number } | null {
    if (!this.emoteShown) return null;
    const progress = (now - this.emoteShown.at) / EMOTE_MS;
    if (progress < 0 || progress >= 1) return null;
    return { emote: this.emoteShown.emote, progress };
  }

  /** Avancement de la caresse en cours (0 → 1), null s'il n'y en a pas. */
  petting(now: number): number | null {
    const progress = (now - this.pettedAt) / PET_MS;
    return progress >= 0 && progress < 1 ? progress : null;
  }

  get isMoving(): boolean {
    return this.stepStartedAt !== 0;
  }

  /**
   * Déplace l'entité ; seuls un pas vers une case voisine (en marchant ou en courant) et un saut de deux
   * cases en ligne droite (par-dessus une bordure) sont animés, le reste est une téléportation.
   * `stepMs` : durée du pas, si elle n'est pas celle d'un joueur (PNJ).
   */
  moveTo({ x, y }: Position, animate = true, running = false, stepMs?: number): void {
    const dx = Math.abs(this.x - x), dy = Math.abs(this.y - y);
    const adjacent = dx + dy === 1;
    const jump = (dx === 2 && dy === 0) || (dx === 0 && dy === 2);
    const { trail } = this;
    if (animate && (adjacent || jump)) {
      const now = performance.now();
      if (!this.stepStartedAt && now - this.stoppedAt > MOTION_GRACE_MS) this.motionStartedAt = now;
      this.fromX = this.renderX;
      this.fromY = this.renderY;
      this.stepStartedAt = now;
      this.running = running;
      this.stepMs = jump ? JUMP_MS : stepMs ?? (running ? RUN_STEP_MS : WALK_STEP_MS);
      this.jumping = jump;
      // Le suiveur prend la case que le joueur quitte, au même rythme que lui.
      Object.assign(trail, { fromX: trail.renderX, fromY: trail.renderY, x: this.x, y: this.y });
      if (trail.x !== trail.fromX || trail.y !== trail.fromY) trail.dir = directionOf(trail.x - trail.fromX, trail.y - trail.fromY);
    } else {
      this.renderX = this.fromX = x;
      this.renderY = this.fromY = y;
      this.stepStartedAt = 0;
      this.hop = 0;
      // Téléportation : le suiveur réapparaît sous le joueur et repartira derrière lui au premier pas.
      Object.assign(trail, { x, y, fromX: x, fromY: y, renderX: x, renderY: y });
    }
    this.x = x;
    this.y = y;
  }

  /** Met à jour la position rendue ; renvoie l'avancement du pas en cours (0 si immobile). */
  update(now: number): number {
    if (!this.stepStartedAt) {
      if (this.hoppedAt !== null) {
        const progress = (now - this.hoppedAt) / TALK_HOP_MS;
        if (progress >= 1) this.hoppedAt = null;
        this.hop = progress >= 0 && progress < 1 ? Math.sin(progress * Math.PI) * TALK_HOP_HEIGHT : 0;
      }
      return 0;
    }
    const progress = (now - this.stepStartedAt) / this.stepMs;
    const { trail } = this;
    if (progress >= 1) {
      this.stoppedAt = now;
      this.renderX = this.x;
      this.renderY = this.y;
      trail.renderX = trail.x;
      trail.renderY = trail.y;
      this.stepStartedAt = 0;
      this.hop = 0;
      return 0;
    }
    this.hop = this.jumping ? Math.sin(progress * Math.PI) * JUMP_HEIGHT : 0;
    this.renderX = this.fromX + (this.x - this.fromX) * progress;
    this.renderY = this.fromY + (this.y - this.fromY) * progress;
    trail.renderX = trail.fromX + (trail.x - trail.fromX) * progress;
    trail.renderY = trail.fromY + (trail.y - trail.fromY) * progress;
    return progress;
  }

  /** En mouvement (ou entre deux pas enchaînés). */
  private isInMotion(now: number): boolean {
    return this.stepStartedAt !== 0 || now - this.stoppedAt < MOTION_GRACE_MS;
  }

  /** Sens dans lequel le dessiner : le sien, ou un quart de tour de plus tous les SPIN_MS en glissant. */
  facing(now: number): Direction {
    if (!this.sliding || !this.isInMotion(now)) return this.dir;
    return SPIN[(SPIN.indexOf(this.dir) + Math.floor(now / SPIN_MS)) % SPIN.length]!;
  }

  /** Animation à jouer et temps écoulé depuis son début (marche ou course pendant les pas, sinon immobile). */
  animation(now: number): { animation: CharacterAnimation; elapsed: number } {
    // En glissant sur la glace ou en surfant (assis sur son {monstre}), on ne marche pas.
    if (!this.sliding && !this.surf && this.isInMotion(now)) {
      return { animation: this.running ? 'run' : 'walk', elapsed: now - this.motionStartedAt };
    }
    return { animation: 'idle', elapsed: now - this.stoppedAt };
  }

  say(text: string): void {
    this.bubbleText = text;
    this.bubbleUntil = performance.now() + BUBBLE_MS;
  }

  bubble(now: number): string | null {
    return now < this.bubbleUntil ? this.bubbleText : null;
  }
}
